import React, { useEffect, useMemo } from 'react';
import { DatePicker, Form, Input, InputNumber, Modal, Select, Typography } from 'antd';
import dayjs from 'dayjs';
import type { Planting } from '../../../../models/Planting';
import type { PlantingDispositionKind, PlantingDispositionPayload } from '../../../../api/plantingsApi';

const { Text } = Typography;

type FormValues = {
  kind: PlantingDispositionKind;
  quantityAffected?: number;
  effectiveDate: dayjs.Dayjs;
  reason: string;
};

interface Props {
  open: boolean;
  planting: Planting;
  loading: boolean;
  onCancel: () => void;
  onSubmit: (payload: PlantingDispositionPayload) => Promise<void>;
}

const OPTIONS: Array<{ value: PlantingDispositionKind; label: string }> = [
  { value: 'partial_writeoff', label: 'Списать часть' },
  { value: 'written_off', label: 'Списать полностью' },
  { value: 'partial_sale', label: 'Продать часть' },
  { value: 'sold', label: 'Продать полностью' },
];

const PlantingDispositionModal: React.FC<Props> = ({
  open,
  planting,
  loading,
  onCancel,
  onSubmit,
}) => {
  const [form] = Form.useForm<FormValues>();
  const quantity = Math.max(1, Number(planting.quantity || 1));
  const isBatch = planting.plantingType === 'batch' || quantity > 1;
  const kind = Form.useWatch('kind', form);
  const isFullKind = kind === 'written_off' || kind === 'sold';

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      kind: isBatch ? 'partial_writeoff' : 'written_off',
      quantityAffected: quantity,
      effectiveDate: dayjs(),
      reason: '',
    });
  }, [form, isBatch, open, quantity]);

  useEffect(() => {
    if (!open || !isFullKind) return;
    form.setFieldValue('quantityAffected', quantity);
  }, [form, isFullKind, open, quantity]);

  const quantityRules = useMemo(() => {
    if (!isBatch) return [];
    return [
      { required: true, message: 'Укажите количество' },
      { type: 'number' as const, min: 1, max: quantity, message: `От 1 до ${quantity}` },
    ];
  }, [isBatch, quantity]);

  const handleOk = async () => {
    const values = await form.validateFields();
    await onSubmit({
      kind: values.kind,
      quantityAffected: isBatch ? values.quantityAffected : 1,
      effectiveDate: values.effectiveDate.format('YYYY-MM-DD'),
      reason: values.reason.trim(),
    });
  };

  return (
    <Modal
      title="Выбытие посадки"
      open={open}
      okText="Применить"
      cancelText="Отмена"
      confirmLoading={loading}
      onCancel={onCancel}
      onOk={handleOk}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item name="kind" label="Операция" rules={[{ required: true, message: 'Выберите операцию' }]}>
          <Select options={OPTIONS} />
        </Form.Item>

        {isBatch && (
          <Form.Item name="quantityAffected" label="Количество" rules={quantityRules}>
            <InputNumber min={1} max={quantity} precision={0} disabled={isFullKind} addonAfter="шт" style={{ width: '100%' }} />
          </Form.Item>
        )}

        <Text type="secondary">Текущий остаток: {quantity} шт</Text>

        <Form.Item
          name="effectiveDate"
          label="Дата"
          rules={[{ required: true, message: 'Укажите дату' }]}
          style={{ marginTop: 16 }}
        >
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="reason"
          label="Причина"
          rules={[
            { required: true, message: 'Укажите причину' },
            { transform: (value) => String(value || '').trim(), min: 1, message: 'Укажите причину' },
          ]}
        >
          <Input.TextArea rows={3} placeholder="Например, продажа партии или списание брака" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default PlantingDispositionModal;
